import { usePageSEO } from '@/hooks/usePageSEO';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Hero from './components/Hero';
import PourquoiDeneiger from './components/PourquoiDeneiger';
import ServicesHiver from './components/ServicesHiver';
import UrgenceExpliquee from './components/UrgenceExpliquee';
import PrixDeneigement from './components/PrixDeneigement';
import Temoignage from './components/Temoignage';
import FAQ from './components/FAQ';
import QuoteForm from './components/QuoteForm';
import BarreUrgence from './components/BarreUrgence';
import CTAFinal from './components/CTAFinal';

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://couvreur-levis.com/' },
    { '@type': 'ListItem', position: 2, name: 'Déneigement toit', item: 'https://couvreur-levis.com/deneigement-toiture' },
  ],
};

const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: 'Déneigement de toiture',
  provider: { '@type': 'LocalBusiness', '@id': 'https://couvreur-levis.com/#business' },
  areaServed: { '@type': 'City', name: 'Lévis' },
  description: "Déneigement de toiture à Lévis en hiver. Retrait neige, glace, prévention dommages.",
  offers: {
    '@type': 'AggregateOffer',
    priceCurrency: 'CAD',
    lowPrice: '250',
    highPrice: '1500',
    offerCount: '5',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: "À partir de combien de neige faut-il déneiger ?",
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Pour les toits plats, on recommande le déneigement à partir de 45-60 cm d'accumulation. Pour les toits en pente, 30-45 cm peut déjà créer un barrage de glace dangereux. Après une tempête de verglas ou de neige lourde, mieux vaut appeler rapidement. Ne jouez pas avec la limite — un toit affaissé ou une infiltration coûte beaucoup plus cher qu'un déneigement préventif.",
      },
    },
    {
      '@type': 'Question',
      name: "Pourquoi pas utiliser du sel ou une hache moi-même ?",
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Le sel abîme le bardeau, la membrane, les gouttières et l'environnement. Une hache risque de briser des bardeaux, d'abîmer la membrane d'étanchéité sous-jacente, ou de vous blesser gravement sur une échelle glissante. Le déglaçage à la vapeur est la seule méthode professionnelle qui élimine le glace sans endommager votre toiture. Laissez ça à une équipe assurée et équipée.",
      },
    },
    {
      '@type': 'Question',
      name: "Quelle différence entre déneigement et déglaçage ?",
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Le déneigement retire la neige accumulée sur le toit pour alléger la charge et prévenir l'effondrement. Le déglaçage élimine le glace et le barrage de glace déjà formé, souvent avec de la vapeur chaude. Les deux peuvent être nécessaires : on déneige d'abord, puis on déglaçage si un barrage est présent. On évalue tout ça sur place et on vous donne un plan clair avant de commencer.",
      },
    },
    {
      '@type': 'Question',
      name: "Travaillez-vous les fins de semaine et soirs ?",
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Oui, on intervient 7 jours sur 7 pendant la saison hivernale. Les urgences ne respectent pas les horaires de bureau. Quand un barrage de glace fait couler de l'eau dans votre salon à 21h un samedi soir, on comprend que ça ne peut pas attendre lundi. Appelez-nous — si la situation est urgente, on vient.",
      },
    },
    {
      '@type': 'Question',
      name: "Le déneigement est-il couvert par mon assurance habitation ?",
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Ça dépend de votre police d'assurance. Certaines assureurs couvrent le déneigement si c'est prescrit après un sinistre (infiltration, affaissement). D'autres offrent une couverture préventive. On fournit un rapport d'intervention détaillé avec photos que vous pouvez soumettre à votre assureur. On vous conseille aussi de vérifier avec votre courtier avant la saison hivernale.",
      },
    },
  ],
};

export default function DeneigementPage() {
  usePageSEO({
    title: 'Déneigement de toit Lévis | Hiver | Glace et neige',
    description: 'Déneigement de toiture à Lévis en hiver. Retrait neige et glace, prévention dommages. Intervention rapide. 418-476-9950.',
    urlPath: '/deneigement-toiture',
    keywords: 'déneigement toiture Lévis, retrait neige toit, glace toiture, hiver toiture, couvreur',
    ogImage: '/og/og-deneigement-toiture.jpg',
    ogImageAlt: 'Déneigement de toit à Lévis - Hiver, prévention dommages',
    jsonLdSchema: [breadcrumbSchema, serviceSchema, faqSchema],
  });

  return (
    <div className="font-inter min-h-screen bg-white">
      <Header />
      <main>
        <Hero />
        <PourquoiDeneiger />
        <ServicesHiver />
        <UrgenceExpliquee />
        <PrixDeneigement />
        <Temoignage />
        <FAQ />
        <QuoteForm />
        <BarreUrgence />
        <CTAFinal />
      </main>
      <Footer />
    </div>
  );
}